import { PropsWithChildren, ReactElement, createContext, useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { logger } from '@breathelife/monitoring-frontend';
import { Application, Language, MedicalInterviewProvider, QuestionnaireVersionFlag } from '@breathelife/types';
import {
  StaticSubscriber,
  DEFAULT_TIMEZONE_NAME,
  InsuranceModules,
  PivotAnswerResolver,
  PlatformTypes,
  QuestionnaireEngine,
  RenderingQuestionnaire,
  RenderingType,
  Timezone,
  VersionedAnswers,
} from '@zinnia/form-engine-sdk';

import { useModals } from '../Components/AssistedApplication/ModalsContext';
import { createComprehensivePricingSubscriber } from '../Components/AssistedApplication/Subscribers/comprehensivePricingSubscriber';
import { fetchQuotesAndMaybeProducts } from '../Components/AssistedApplication/Subscribers/fetchQuotesSubscriber';
import { createGenerateParticipantsSubscriber } from '../Components/AssistedApplication/Subscribers/generateParticipantsSubscriber';
import { createJetSubscriber } from '../Components/AssistedApplication/Subscribers/jetSubscriber';
import { sendAnswersToBackend } from '../Components/AssistedApplication/Subscribers/saveAnswersSubscriber';
import { getQuestionnaireEngineConfig } from '../Helpers/questionnaireEngineConfigs';
import { useFetchQuotesMutation } from '../ReactQuery/AssistedApplication/assistedApplication.mutations';
import { useGenerateParticipantsMutation } from '../ReactQuery/ParticipantProcessor/generateParticipants.mutations';
import { useFetchParticipantsWithMedicalInterviewQuery } from '../ReactQuery/Participants/participants.queries';
import { useAAQuestionnaireVersionContext } from './useAAQuestionnaireVersionContext';
import { useCarrierContext } from './useCarrierContext';
import { useDispatch } from './useDispatch';
import { useJetDecisionOutcomes } from './useJetDecisionOutcomes';

export type AssistedApplicationQuestionnaireVersionValue = {
  questionnaireEngine: QuestionnaireEngine;
  renderingQuestionnaire: RenderingQuestionnaire;
  hasActiveSubscribers: boolean;
};

export const QuestionnaireEngineContext = createContext<AssistedApplicationQuestionnaireVersionValue | undefined>(
  undefined,
);

export function useQuestionnaireEngine(): AssistedApplicationQuestionnaireVersionValue {
  const context = useContext(QuestionnaireEngineContext);

  if (typeof context === 'undefined') {
    throw new Error(
      'useQuestionnaireEngine must be used within an AssistedApplicationQuestionnaireVersionContextProvider',
    );
  }

  return context;
}

type Props = PropsWithChildren<{
  application: Application;
}>;

export function QuestionnaireEngineProvider(props: Props): ReactElement | null {
  const { application } = props;
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { pricingFieldIdentifiers, nodeIdToAnswerPathMap, blueprint, questionnaire, flags } =
    useAAQuestionnaireVersionContext();
  const { features } = useCarrierContext();
  const { openSaveAnswersValidationModal } = useModals();
  const queryClient = useQueryClient();
  const { setIsFetching, setJetDecisionOutcomes } = useJetDecisionOutcomes();
  const fetchQuotesMutation = useFetchQuotesMutation();

  const isAuraNextMedicalInterviewEnabled =
    features.medicalInterview.enabled &&
    features.medicalInterview.provider === MedicalInterviewProvider.AuraNext &&
    flags.includes(QuestionnaireVersionFlag.AuraNextMedicalInterview);

  const { data: participants } = useFetchParticipantsWithMedicalInterviewQuery(application?.id, {
    enabled: application && isAuraNextMedicalInterviewEnabled,
  });

  const areAllMedicalInterviewCompleted = useMemo(() => {
    if (!participants) return false;
    return participants.length === 0
      ? false
      : participants.every(
          (participant) =>
            participant.medicalInterview?.status === 'COMPLETED' || participant.medicalInterview?.status === 'SKIPPED',
        );
  }, [participants]);

  // Use a ref to store the current questionnaireEngine instance.
  // This allows us to get the latest answers synchronously before recreating the engine.
  const questionnaireEngineRef = useRef<QuestionnaireEngine | null>(null);

  const [renderingQuestionnaire, setRenderingQuestionnaire] = useState<RenderingQuestionnaire | null>(null);
  const [isQuotesSubscriberActive, setIsQuotesSubscriberActive] = useState<boolean>(false);
  const [isPricingSubscriberActive, setIsPricingSubscriberActive] = useState<boolean>(false);
  const [isGenerateParticipantsSubscriberActive, setIsGenerateParticipantsSubscriberActive] = useState<boolean>(false);
  const [isAnswersSubscriberActive, setIsAnswersSubscriberActive] = useState<boolean>(false);

  const generateParticipantsMutation = useGenerateParticipantsMutation(application.id, flags, true);

  const hasActiveSubscribers = useMemo(() => {
    return (
      isQuotesSubscriberActive ||
      isPricingSubscriberActive ||
      isAnswersSubscriberActive ||
      isGenerateParticipantsSubscriberActive
    );
  }, [
    isQuotesSubscriberActive,
    isPricingSubscriberActive,
    isAnswersSubscriberActive,
    isGenerateParticipantsSubscriberActive,
  ]);

  const questionnaireEngine = useMemo(() => {
    const timezoneResult = Timezone.from(application.timezone || DEFAULT_TIMEZONE_NAME);
    if (timezoneResult.success === false) {
      throw new Error('Could not create the timezone for the application or using the default one.');
    }

    // Get current in-memory answers from the previous engine instance (if it exists),
    // otherwise fall back to database answers. This prevents losing unsaved answers
    // when the engine is recreated due to areAllMedicalInterviewCompleted changing.
    const previousAnswers = questionnaireEngineRef.current?.getAnswerResolverInstance().dump();
    const answersToUse = previousAnswers ?? { v1: application.answers, v2: application.answersV2 };

    const answerResolver = PivotAnswerResolver.from(
      new VersionedAnswers(answersToUse),
      nodeIdToAnswerPathMap,
      blueprint,
      'v1',
      logger,
    );

    // There are performance implications to this object being remade (some internal data structures get initialized when updating answers that are expensive to rebuild)
    // Once the questionnaire and nodeIdToAnswerPathMap are loaded remaking this object should be avoided.
    const subscribers: StaticSubscriber[] = [
      sendAnswersToBackend(application, openSaveAnswersValidationModal, setIsAnswersSubscriberActive, dispatch, t),
      fetchQuotesAndMaybeProducts(
        application,
        dispatch,
        queryClient,
        i18n.language as Language,
        features,
        pricingFieldIdentifiers,
        fetchQuotesMutation,
        setIsQuotesSubscriberActive,
      ),
      createComprehensivePricingSubscriber(
        features,
        application,
        pricingFieldIdentifiers,
        dispatch,
        t,
        setIsPricingSubscriberActive,
      ),
      createJetSubscriber(application.id, setIsFetching, setJetDecisionOutcomes, features, dispatch, t),
      createGenerateParticipantsSubscriber(
        features,
        setIsGenerateParticipantsSubscriberActive,
        generateParticipantsMutation,
      ),
    ];

    const engine = QuestionnaireEngine.from({
      questionnaire: questionnaire,
      answerResolver,
      context: {
        platformTypes: [PlatformTypes.producer],
        insuranceModules: [InsuranceModules.insuranceApplication],
        applicationModes: [application.mode],
      },
      config: getQuestionnaireEngineConfig(application.mode, features),
      timezone: timezoneResult.value,
      applicationContext: application.applicationContext,
      onRenderingQuestionnaireChanged: setRenderingQuestionnaire,
      answerChangedSubscribers: subscribers,
      renderingOptions: {
        readonly: application.submitted,
        renderingType: RenderingType.web,
        displayErrors: !application.submitted,
      },
      blueprint,
      language: i18n.language as Language,
      currentDateOverride: application.submissionDate?.toString() || null,
      cloningStrategy: 'Fast',
      ...(isAuraNextMedicalInterviewEnabled && {
        customComponents: {
          AuraNextMedicalQuestionnaire: {
            validate: () => ({
              isValid: areAllMedicalInterviewCompleted,
            }),
          },
        },
      }),
    });

    // Update the ref synchronously so it's available for the next recreation
    questionnaireEngineRef.current = engine;

    return engine;
  }, [
    application,
    nodeIdToAnswerPathMap,
    blueprint,
    questionnaire,
    features,
    dispatch,
    openSaveAnswersValidationModal,
    flags,
    areAllMedicalInterviewCompleted,
  ]);

  return (
    <QuestionnaireEngineContext.Provider
      value={{
        questionnaireEngine,
        renderingQuestionnaire: renderingQuestionnaire
          ? renderingQuestionnaire
          : questionnaireEngine.renderingQuestionnaire,
        hasActiveSubscribers,
      }}
    >
      {props.children}
    </QuestionnaireEngineContext.Provider>
  );
}
